/* eslint-disable indent */
import styled, { css } from 'styled-components';
import { WHITE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';
import { NOKIA_PURE_TEXT } from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontFamily';

import {
  SPACING_12,
  SPACING_32,
  SPACING_2,
  SPACING_16,
  SPACING_24,
  SPACING_4
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { CAPTION } from '@nokia-csf-uxr/freeform-design-tokens/tokens/typography';
import { DEEP_BORDER_BOX } from 'common';

const partialTrackCommonStyle = (transformOrigin, isTickerThumbDragging, $disableAnimation) => ({
  position: 'absolute',
  height: '100%',
  width: '100%',
  transformOrigin,
  transition: !isTickerThumbDragging && !$disableAnimation && `transform ${SPEED_SLOW} ${TRANSITION_DECELERATE}`
});

const getDefaultTeardropColor = ({ isTicker, $theme }) => {
  const currentColor = isTicker ? $theme.content.tearDrop : $theme.content.normal;
  return css`
    color: ${currentColor};
  `;
};

const getTearWrapperTransform = ({
 vertical, flipIndicatorPosition, isTicker, $rtl
}) => {
  let translate = flipIndicatorPosition ? 'translateY(50%)' : 'translateY(-50%)';
  if (vertical) {
    if (!$rtl) {
      translate = flipIndicatorPosition ? 'translateX(55%) translateY(0%)' : 'translateX(-55%) translateY(0%)';
    } else {
      translate = flipIndicatorPosition ? 'translateX(-55%) translateY(0%)' : 'translateX(55%) translateY(0%)';
    }
  }
  const scale = isTicker ? 'scale(0)' : 'scale(1)';
  return { translate, scale };
};

const ThumbContent = styled.div`
  ${({ disabled, $theme }) =>
    css`
      background-color: ${!disabled ? $theme.color : $theme.disabled};
    `}
  width: ${SPACING_12};
  height: ${SPACING_12};
  border-radius: 50%;
  outline: none;
  &:hover {
    ${props =>
      !props.disabled &&
      css`
        box-shadow: ${props.isTicker ? 'none' : `0 0 0 0.375rem ${props.$theme.halo.hover}`};
      `}
  }
  &:active {
    ${props =>
      !props.disabled &&
      css`
        box-shadow: ${props.isTicker ? 'none' : `0 0 0 0.375rem ${props.$theme.halo.active}`};
      `}
  }
`;

const Thumb = styled.div`
  ${DEEP_BORDER_BOX}
  outline: none;
  padding: 0.1875rem;
  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 50%;
  position: absolute;
  ${props =>
    props.vertical &&
    css`
      left: ${SPACING_2};
    `}
  ${props =>
    css`
      transition: ${!props.$disableAnimation ? `height ${SPEED_SLOW} ${TRANSITION_DECELERATE}` : ''} ${!props.isTickerThumbDragging && !props.$disableAnimation ? `, transform ${SPEED_SLOW} ${TRANSITION_DECELERATE}` : ''};
    `}

  transform-origin:  ${props => (props.vertical ? 'left' : 'top')};

  &:focus-within {
    ${props =>
      css`
        border: ${!props.disabled && !props.isTicker ? `${SPACING_2} solid ${props.$theme.focus.border}` : 'none'};
      `}
  }

  `;

const TearWrapper = styled.div`
  outline: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  position: absolute;
  ${({ $disableAnimation }) => !$disableAnimation && css`
    transition: transform ${SPEED_SLOW} ${TRANSITION_DECELERATE};
  `}
  ${({
 vertical, isTicker, $rtl, alwaysShowTeardrop, flipIndicatorPosition
}) => {
    const { translate, scale } = getTearWrapperTransform({
 vertical, isTicker, flipIndicatorPosition, $rtl
});
    return css`
      transform: ${translate} ${alwaysShowTeardrop ? 'scale(1)' : scale};
    `;
  }}

  ${Thumb}:focus &, ${Thumb}:focus-within & {
    height: auto;
    cursor: pointer;
    ${({
 vertical, isTicker, flipIndicatorPosition, $rtl
}) => {
      const { translate } = getTearWrapperTransform({
 vertical, isTicker, $rtl, flipIndicatorPosition
});
      return isTicker
        ? css`
            transform: ${translate} scale(1);
          `
        : '';
    }}
  }
  ${({
 vertical, isTicker, flipIndicatorPosition, $rtl, isThumbHover
}) => {
    const { translate } = getTearWrapperTransform({
 vertical, isTicker, $rtl, flipIndicatorPosition
});
    return isTicker && isThumbHover
      ? css`
          transform: ${translate} scale(1);
        `
      : '';
  }}
`;

const HiddenInput = styled.input`
  left: 0;
  width: 100%;
  bottom: 0;
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

export default {
  Thumb,
  TearWrapper,
  ThumbContent,
  HiddenInput,
  FromControlInput: styled.input``,
  ValueWrapper: styled.span``,
  Wrapper: styled.div`
    ${({ vertical }) =>
      vertical
        ? css`
            width: fit-content;
            height: 100%;
            flex-direction: column;
          `
        : css`
            width: 100%;
          `}

    display: flex;
    justify-content: space-between;
    font-family: ${NOKIA_PURE_TEXT};
    font-size: ${SPACING_12};
    ${props =>
      !props.RTL
        ? css`
            direction: ltr;
          `
        : css`
            direction: rtl;
          `}
  `,

  TrackWrapper: styled.div`
    ${({ vertical }) =>
      vertical
        ? css`
            height: 100%;
            width: ${SPACING_4};
            margin: ${SPACING_12} auto;
          `
        : css`
            height: ${SPACING_4};
            width: 100%;
            margin: auto ${SPACING_12};
          `}
  `,

  Track: styled.div`
    ${({ $theme, disabled, isRangeSelection }) =>
      isRangeSelection &&
      css`
        background-color: ${!disabled ? $theme.on.color : $theme.on.disabled};
      `}
    ${({ vertical }) =>
      vertical
        ? css`
            width: ${SPACING_4};
            height: 100%;
          `
        : css`
            height: ${SPACING_4};
          `}
    flex: 1;
    align-self: center;
    position: relative;
    ${({ $disableAnimation }) => !$disableAnimation && css`
      transition: background ${SPEED_SLOW} ${TRANSITION_DECELERATE};
    `}
  `,

  FrontTrack: styled.div`
    ${({ isTickerThumbDragging, $rtl, vertical, $disableAnimation }) => {
      let transformOrigin = $rtl ? 'right' : 'left';
      if (vertical) transformOrigin = 'bottom';
      return partialTrackCommonStyle(transformOrigin, isTickerThumbDragging, $disableAnimation);
    }}
    ${({ disabled, $theme, isRangeSelection }) =>
      !isRangeSelection &&
      css`
        background-color: ${!disabled ? $theme.on.color : $theme.on.disabled};
      `} 
    ${({ isRangeSelection, $theme }) =>
      isRangeSelection &&
      css`
        background-color: ${$theme.off.color};
      `} 
  `,
  RearTrack: styled.div`
    ${({ isTickerThumbDragging, $rtl, vertical, $disableAnimation }) => {
      let transformOrigin = $rtl ? 'left' : 'right';
      if (vertical) transformOrigin = 'top';
      return partialTrackCommonStyle(transformOrigin, isTickerThumbDragging, $disableAnimation);
    }}
    ${({ $theme }) =>
      css`
        background-color: ${$theme.off.color};
      `}
  `,
  ThumbContentWrapper: styled.div`
    z-index: 1;
    cursor: pointer;
    ${({ isTicker, isThumbHover, alwaysShowTeardrop }) =>
      isTicker &&
      (alwaysShowTeardrop || isThumbHover) &&
      css`
        opacity: 0;
        overflow: hidden;
      `}
    &:hover, ${Thumb}:focus-within & {
      ${({ isTicker, disabled }) =>
        !disabled &&
        isTicker &&
        css`
          opacity: 0;
          overflow: hidden;
        `}
    }
  `,
  Label: styled.div`
    ${CAPTION}
    line-height: ${SPACING_24};
    flex-grow: 0;
    user-select: none;
    ${({ vertical }) =>
      vertical &&
      css`
        text-align: center;
      `}
    ${props =>
      css`
        color: ${!props.disabled ? props.$theme.label.color : props.$theme.label.disabled};
      `}
    ${props =>
      !props.$rtl
        ? css`
            direction: ltr;
          `
        : css`
            direction: rtl;
          `}
  `,

  Marker: styled.div`
    ${({ isLeftEnd, isRightEnd, vertical }) => {
      const sizeAndRotation = {
        width: SPACING_4,
        height: SPACING_4,
        borderRadius: SPACING_4,
        transformOrigin: 'bottom'
      };
      if (isLeftEnd || isRightEnd) {
        sizeAndRotation.height = SPACING_2;
        sizeAndRotation.borderRadius = `${SPACING_2} ${SPACING_2} 0 0`;
        if (vertical) {
          if (isLeftEnd) {
            sizeAndRotation.transform = 'rotate(180deg)';
          }
        } else if (!vertical) {
          if (isLeftEnd) {
            sizeAndRotation.transform = 'rotate(90deg)';
          } else {
            sizeAndRotation.transform = 'rotate(270deg)';
          }
        }
      }
      return sizeAndRotation;
    }}

    position: absolute;
    opacity: 0.54;
    background-color: ${WHITE};
  `,

  SliderLabel: styled.div`
    min-width: ${SPACING_24};
    display: flex;
  `,
  DefaultTeardrop: styled.div`
    ${CAPTION};
    position: relative;
    ${({ isTicker, flipIndicatorPosition }) => {
      const spacing = isTicker ? SPACING_2 : '0.375rem';
      if (flipIndicatorPosition) {
        return { top: spacing };
      }
      return { bottom: spacing };
    }};
    left: 0;
    min-width: ${SPACING_32};
    height: ${SPACING_32};
    line-height: 2.1rem;
    text-align: center;
    user-select: none;
    border-radius: 50%;
    outline: none;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      width: ${SPACING_32};
      height: ${SPACING_32};
      border-radius: 50%;
    }

    ${({ vertical }) =>
      vertical &&
      css`
        bottom: 0;
        top: 0;
        line-height: ${SPACING_32};
      `};
    &:focus {
      &::before {
        ${props =>
          !props.disabled &&
          props.isTicker &&
          css`
            border: ${`${SPACING_2} solid ${props.$theme.focus.border}`};
            width: 1.75rem;
            height: 1.75rem;
          `}
      }
    }
    &:hover {
      &::before {
        ${props =>
          !props.disabled &&
          css`
            box-shadow: ${!props.isTicker ? 'none' : `inset 0 0 0 ${SPACING_16} ${props.$theme.halo.hover}`};
          `}
      }
    }
    &:active {
      &::before {
        ${props =>
          !props.disabled &&
          css`
            box-shadow: ${!props.isTicker ? 'none' : `inset 0 0 0 ${SPACING_16} ${props.$theme.halo.active}`};
          `}
      }
    }

    ${props => getDefaultTeardropColor(props)}
    &::after {
      content: '';
      position: absolute;
      left: 0.375rem;
      bottom: 0.375rem;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 0 50% 50% 50%;
      ${({ vertical, $rtl, flipIndicatorPosition }) => {
        let deg = flipIndicatorPosition ? '45deg' : '225deg';
        if (vertical) {
          deg = $rtl || flipIndicatorPosition ? '315deg' : '135deg';
        }
        return vertical ? `transform: rotate(${deg}); bottom: 0.36rem;` : `transform: rotate(${deg});`;
      }}
      z-index: -1;
      ${props =>
        css`
          background-color: ${!props.disabled && props.isTicker ? props.$theme.content.tearDropBackground : 'none'};
          background-color: ${props.disabled && props.isTicker ? props.$theme.content.tearDropDisabled : 'none'};
        `}
    }
  `
};
